/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect } from "react";
import { Grid, Stack, Box } from "@mui/material";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import {
  PricingInterface,
  HeaderEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { pricingFormYUPValidation } from "utils/form.utils";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import FormField from "components/generals/FormInput";
import FormSelectInput from "components/generals/FormSelectInput";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { TaxType, TaxCalculationTypeEnum } from "@malmur-technologies/medicalsystem-shared";
import FormActionButtons from "./custom/FormActionButtons";
import { useMaterialUIController } from 'context/ThemeContext';
import CurrencyInput from "components/generals/CurrencyInput";
import PercentageInput from "components/generals/PercentageInput";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";
import { useSelector } from "react-redux";

import {
  ExtendedPricingInterface,
} from "interfaces/extended.pricing.interface";

/**
 * Properties for the BasePriceUpdater component.
 */
interface BasePriceUpdaterProps {
  values: Partial<ExtendedPricingInterface>;
  setFieldValue: (field: string, value: number, shouldValidate?: boolean) => void;
  calculateBasePrice: (
    finalPrice: number,
    taxType?: TaxType,
    taxAmount?: number,
    taxCalculationType?: TaxCalculationTypeEnum.AMOUNT | TaxCalculationTypeEnum.PERCENTAGE,
    altTaxType?: TaxType,
    altTaxAmount?: number,
    alternativeTaxCalculationType?:
      | TaxCalculationTypeEnum.AMOUNT
      | TaxCalculationTypeEnum.PERCENTAGE
  ) => number;
}

/**
 * Component that automatically recalculates base price when final price or taxes change.
 * Uses useEffect to monitor pricing fields and update base price accordingly.
 * 
 * Calculation Flow:
 * - Monitors finalPrice, taxAmount, and alternativeTaxAmount
 * - Calls calculateBasePrice with current values
 * - Updates price field if difference exceeds $0.01
 * - Prevents infinite loops by not validating on setFieldValue
 * 
 * @param {BasePriceUpdaterProps} props - Component properties
 * @param {ExtendedPricingInterface} props.values - Current form values
 * @param {Function} props.setFieldValue - Formik field setter
 * @param {Function} props.calculateBasePrice - Base price calculation function
 * 
 * @returns {null} No visual output - effect only
 */
const BasePriceUpdater: React.FC<BasePriceUpdaterProps> = ({
  values,
  setFieldValue,
  calculateBasePrice,
}) => {
  useEffect(() => {
    const taxCalculationType = values.taxCalculationType;
    const alternativeTaxCalculationType = values.alternativeTaxCalculationType;

    const newBasePrice = calculateBasePrice(
      Number(values.finalPrice) || 0,
      values.taxAmountType,
      Number(values.taxAmount) || 0,
      taxCalculationType,
      values.alternativeTaxAmountType,
      Number(values.alternativeTaxAmount) || 0,
      alternativeTaxCalculationType
    );

    if (Math.abs((values.price || 0) - newBasePrice) > 0.01) {
      setFieldValue("price", newBasePrice, false);
    }
  }, [
    values.finalPrice,
    values.taxAmountType,
    values.taxAmount,
    values.alternativeTaxAmountType,
    values.alternativeTaxAmount,
    values.taxCalculationType,
    values.alternativeTaxCalculationType,
    setFieldValue,
    calculateBasePrice,
  ]);

  return null;
};

/**
 * Medical service pricing form with complex tax calculations.
 * 
 * Features:
 * - CPT code and service description
 * - Multi-level pricing structure:
 *   - Base price (before taxes)
 *   - Primary tax (amount or percentage)
 *   - Alternative/secondary tax
 *   - Final price (with all taxes applied)
 * - Automatic price calculations:
 *   - Calculate final price from base + taxes
 *   - Calculate base price from final price (reverse calculation)
 *   - Support for both amount and percentage taxes
 * - Tax configuration:
 *   - Tax calculation type (AMOUNT or PERCENTAGE)
 *   - Multiple tax types support
 *   - Alternative tax options
 * - Currency input formatting
 * - Percentage input validation
 * - Form validation with Yup schema
 * - Permission-based access control
 * - Read-only mode for patients and doctors
 * 
 * Tax Calculation Logic:
 * - Forward: final = base + (tax1) + (tax2)
 * - Reverse: base = final - (tax1) - (tax2)
 * - Percentage taxes: tax = base × (rate / 100)
 * - Amount taxes: tax = fixed amount
 * 
 * @param model - Pricing data to edit (or new pricing template)
 * @param action - Form action (NEW, MODIFY)
 * @param onCloseAction - Callback when form is closed
 * @param setModelList - Callback to update pricing list
 * @param modelList - Current list of pricing records
 * @returns Pricing form with tax calculations
 */
const PricingForm: React.FC<FormProps<PricingInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  const isReadOnlyUser = useSelector(selectIsPatientOrDoctor);
  const isNew = action === FormActionType.NEW;
  const isView = (action === FormActionType.READ_ONLY) || isReadOnlyUser;

  const taxTypeOptions = [
    { value: "VAT" as const, label: t("pricingForm.vat") },
    { value: "GST" as const, label: t("pricingForm.gst") },
    { value: "EXEMPT" as const, label: t("pricingForm.exempt") },
    { value: "OTHER" as const, label: t("pricingForm.other") },
    { value: "NONE" as const, label: t("pricingForm.none") },
  ];

  const calcModeOptions = [
    { value: TaxCalculationTypeEnum.AMOUNT, label: t("pricingForm.amount") },
    { value: TaxCalculationTypeEnum.PERCENTAGE, label: t("pricingForm.percentage") },
  ];

  const getCalculationTypeDisplayValue = (calculationType: TaxCalculationTypeEnum): string => {
    if (calculationType === TaxCalculationTypeEnum.PERCENTAGE) {
      return t("pricingForm.percentage");
    }
    return t("pricingForm.amount");
  };

  const shouldShowTaxFields = (taxType?: TaxType): boolean => {
    return taxType !== undefined &&
      taxType !== TaxType.EXEMPT &&
      taxType !== TaxType.NONE &&
      taxType !== null;
  };

  const calculateBasePrice = (
    finalPrice: number,
    taxType?: TaxType,
    taxAmount?: number,
    taxCalculationType?: TaxCalculationTypeEnum.AMOUNT | TaxCalculationTypeEnum.PERCENTAGE,
    altTaxType?: TaxType,
    altTaxAmount?: number,
    alternativeTaxCalculationType?:
      | TaxCalculationTypeEnum.AMOUNT
      | TaxCalculationTypeEnum.PERCENTAGE
  ): number => {
    let basePrice = finalPrice || 0;
    let totalTaxPercentage = 0;
    let totalTaxAmount = 0;

    if (
      taxType &&
      taxType !== TaxType.EXEMPT &&
      taxType !== TaxType.NONE &&
      taxAmount !== undefined &&
      taxAmount > 0
    ) {
      if (taxCalculationType === TaxCalculationTypeEnum.PERCENTAGE) {
        totalTaxPercentage += taxAmount;
      } else {
        totalTaxAmount += taxAmount;
      }
    }

    if (
      altTaxType &&
      altTaxType !== TaxType.EXEMPT &&
      altTaxType !== TaxType.NONE &&
      altTaxAmount !== undefined &&
      altTaxAmount > 0
    ) {
      if (alternativeTaxCalculationType === TaxCalculationTypeEnum.PERCENTAGE) {
        totalTaxPercentage += altTaxAmount;
      } else {
        totalTaxAmount += altTaxAmount;
      }
    }

    basePrice = finalPrice - totalTaxAmount;

    if (totalTaxPercentage > 0) {
      basePrice = basePrice / (1 + totalTaxPercentage / 100);
    }

    return Number(Math.max(0, basePrice).toFixed(2));
  };

  const submitHandler = (values: Partial<ExtendedPricingInterface>) => {
    const method: Method = isNew ? "POST" : "PUT";
    const successMessageText = isNew ? t("pricingForm.pricingCreated") : t("pricingForm.pricingUpdated");

    const finalPrice = Number(values.finalPrice) || 0;
    const taxAmountRaw = values.taxAmount ? Number(values.taxAmount) : 0;
    const alternativeTaxAmountRaw = values.alternativeTaxAmount
      ? Number(values.alternativeTaxAmount)
      : 0;
    const taxCalculationType = values.taxCalculationType || TaxCalculationTypeEnum.AMOUNT;
    const alternativeTaxCalculationType = values.alternativeTaxCalculationType || TaxCalculationTypeEnum.AMOUNT;

    // IMPORTANT: Calculate base price HERE to ensure it's up to date before submit
    const basePrice = calculateBasePrice(
      finalPrice,
      values.taxAmountType,
      taxAmountRaw,
      taxCalculationType,
      values.alternativeTaxAmountType,
      alternativeTaxAmountRaw,
      alternativeTaxCalculationType
    );

    const computedTaxAmount =
      taxCalculationType === TaxCalculationTypeEnum.PERCENTAGE
        ? basePrice * (taxAmountRaw / 100)
        : taxAmountRaw;

    const computedAlternativeTaxAmount =
      alternativeTaxCalculationType === TaxCalculationTypeEnum.PERCENTAGE
        ? basePrice * (alternativeTaxAmountRaw / 100)
        : alternativeTaxAmountRaw;

    const finalTaxAmount = taxCalculationType === TaxCalculationTypeEnum.PERCENTAGE
      ? computedTaxAmount
      : taxAmountRaw;

    const finalAlternativeTaxAmount = alternativeTaxCalculationType === TaxCalculationTypeEnum.PERCENTAGE
      ? computedAlternativeTaxAmount
      : alternativeTaxAmountRaw;

    const submissionModel: Partial<ExtendedPricingInterface> = {
      ...values,
      price: basePrice,
      finalPrice,
      taxAmount: finalTaxAmount,
      alternativeTaxAmount: finalAlternativeTaxAmount,
      taxCalculationType,
      alternativeTaxCalculationType,
      computedTaxAmount,
      computedAlternativeTaxAmount,
    };

    if (isNew) {
      delete submissionModel.id;
    }
    if (!submissionModel.taxAmountType) {
      submissionModel.taxAmountType = TaxType.OTHER;
    }

    const headerArray = submissionModel.id
      ? [{ header: HeaderEnum.RESOURCE_ID, value: submissionModel.id }]
      : [];

    sendRequest(
      CoreAPI_URLs.PRICING_GENERAL_OPERATIONS,
      onPricingUpdateHandler,
      method,
      JSON.stringify(submissionModel),
      headerArray,
      { successMessage: successMessageText }
    );
  };

  const onPricingUpdateHandler = (pricing: PricingInterface) => {
    if (!isNew) {
      setModelList(modelList.map((item) => (item.id === pricing.id ? pricing : item)));
    } else {
      setModelList((prevList) => [...prevList, pricing]);
    }
    onCloseAction();
  };

  const getFormTitle = (): string => {
    if (!isNew) {
      return t("pricingForm.viewTitle");
    }
    return t("pricingForm.title");
  };

  const extendedModel = model as Partial<ExtendedPricingInterface>;
  const initialValues: Partial<ExtendedPricingInterface> = {
    ...extendedModel,
    taxCalculationType: TaxCalculationTypeEnum.AMOUNT,
    alternativeTaxCalculationType: TaxCalculationTypeEnum.AMOUNT,
  };

  return (
    <FormikForm
      onSubmit={submitHandler}
      initialValues={initialValues}
      validationSchema={pricingFormYUPValidation(t)}
    >
      {({ values, errors, touched, setFieldValue }: FormikProps<Partial<ExtendedPricingInterface>>) => (
        <Form id="pricingForm" autoComplete="off">
          <BasePriceUpdater
            values={values}
            setFieldValue={setFieldValue}
            calculateBasePrice={calculateBasePrice}
          />

          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <AttachMoneyIcon fontSize="large" />
            <H4>{getFormTitle()}</H4>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <FormField
                type="text"
                label={t("pricingForm.cpt")}
                name="cpt"
                value={values.cpt || ""}
                error={errors.cpt && touched.cpt}
                disabled={isView}
                inputProps={{
                  readOnly: isView,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormField
                type="text"
                label={t("pricingForm.name")}
                name="name"
                value={values.name || ""}
                error={errors.name && touched.name}
                disabled={isView}
                inputProps={{
                  readOnly: isView,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CurrencyInput
                label={t("pricingForm.finalPrice")}
                name="finalPrice"
                value={values.finalPrice || 0}
                error={!!(errors.finalPrice && touched.finalPrice)}
                helperText={errors.finalPrice && touched.finalPrice ? errors.finalPrice : ""}
                onChange={(value) => setFieldValue("finalPrice", value)}
                disabled={isView}
                fullWidth
                inputProps={{
                  style: { textAlign: "right", color: "green", fontWeight: "bold" },
                  readOnly: isView,
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label={t("pricingForm.description")}
                name="description"
                value={values.description || ""}
                error={errors.description && touched.description}
                multiline
                rows={3}
                disabled={isView}
                inputProps={{
                  readOnly: isView,
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {isView ? (
                <FormField
                  type="text"
                  label={t("pricingForm.taxType")}
                  name="taxAmountType"
                  value={taxTypeOptions.find(option => option.value === values.taxAmountType)?.label || ""}
                  disabled={true}
                  inputProps={{
                    readOnly: true,
                  }}
                />
              ) : (
                <FormSelectInput
                  label={t("pricingForm.taxType")}
                  name="taxAmountType"
                  selectArray={taxTypeOptions}
                  defaultValue={values.taxAmountType}
                />
              )}
            </Grid>

            {shouldShowTaxFields(values.taxAmountType) && (
              <>
                <Grid item xs={12} sm={4}>
                  {values.taxCalculationType === TaxCalculationTypeEnum.PERCENTAGE ? (
                    <PercentageInput
                      label={t("pricingForm.taxAmount")}
                      name="taxAmount"
                      value={values.taxAmount || 0}
                      error={!!(errors.taxAmount && touched.taxAmount)}
                      helperText={errors.taxAmount && touched.taxAmount ? errors.taxAmount : ""}
                      onChange={(value) => setFieldValue("taxAmount", value)}
                      fullWidth
                      maxValue={100}
                      minValue={0}
                      disabled={isView}
                      inputProps={{
                        readOnly: isView,
                      }}
                    />
                  ) : (
                    <CurrencyInput
                      label={t("pricingForm.taxAmount")}
                      name="taxAmount"
                      value={values.taxAmount || 0}
                      error={!!(errors.taxAmount && touched.taxAmount)}
                      helperText={errors.taxAmount && touched.taxAmount ? errors.taxAmount : ""}
                      onChange={(value) => setFieldValue("taxAmount", value)}
                      fullWidth
                      disabled={isView}
                      inputProps={{
                        readOnly: isView,
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {isView ? (
                    <FormField
                      type="text"
                      label={t("pricingForm.taxCalculationType")}
                      name="taxCalculationType"
                      value={getCalculationTypeDisplayValue(values.taxCalculationType || TaxCalculationTypeEnum.AMOUNT)}
                      disabled={true}
                      inputProps={{
                        readOnly: true,
                      }}
                    />
                  ) : (
                    <FormSelectInput
                      id="cy-taxCalculationType"
                      label={t("pricingForm.taxCalculationType")}
                      name="taxCalculationType"
                      selectArray={calcModeOptions}
                      defaultValue={TaxCalculationTypeEnum.AMOUNT}
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {isView ? (
                <FormField
                  type="text"
                  label={t("pricingForm.alternativeTaxType")}
                  name="alternativeTaxAmountType"
                  value={taxTypeOptions.find(option => option.value === values.alternativeTaxAmountType)?.label || ""}
                  disabled={true}
                  inputProps={{
                    readOnly: true,
                  }}
                />
              ) : (
                <FormSelectInput
                  id="cy-alternativeTaxAmountType"
                  label={t("pricingForm.alternativeTaxType")}
                  name="alternativeTaxAmountType"
                  selectArray={taxTypeOptions}
                  defaultValue={values.alternativeTaxAmountType}
                />
              )}
            </Grid>

            {shouldShowTaxFields(values.alternativeTaxAmountType) && (
              <>
                <Grid item xs={12} sm={4}>
                  {values.alternativeTaxCalculationType === TaxCalculationTypeEnum.PERCENTAGE ? (
                    <PercentageInput
                      label={t("pricingForm.alternativeTaxAmount")}
                      name="alternativeTaxAmount"
                      value={values.alternativeTaxAmount || 0}
                      error={!!(errors.alternativeTaxAmount && touched.alternativeTaxAmount)}
                      helperText={errors.alternativeTaxAmount && touched.alternativeTaxAmount ? errors.alternativeTaxAmount : ""}
                      onChange={(value) => setFieldValue("alternativeTaxAmount", value)}
                      fullWidth
                      maxValue={100}
                      minValue={0}
                      disabled={isView}
                      inputProps={{
                        readOnly: isView,
                      }}
                    />
                  ) : (
                    <CurrencyInput
                      label={t("pricingForm.alternativeTaxAmount")}
                      name="alternativeTaxAmount"
                      value={values.alternativeTaxAmount || 0}
                      error={!!(errors.alternativeTaxAmount && touched.alternativeTaxAmount)}
                      helperText={errors.alternativeTaxAmount && touched.alternativeTaxAmount ? errors.alternativeTaxAmount : ""}
                      onChange={(value) => setFieldValue("alternativeTaxAmount", value)}
                      fullWidth
                      disabled={isView}
                      inputProps={{
                        readOnly: isView,
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {isView ? (
                    <FormField
                      type="text"
                      label={t("pricingForm.alternativeTaxCalculationType")}
                      name="alternativeTaxCalculationType"
                      value={getCalculationTypeDisplayValue(values.alternativeTaxCalculationType || TaxCalculationTypeEnum.AMOUNT)}
                      disabled={true}
                      inputProps={{
                        readOnly: true,
                      }}
                    />
                  ) : (
                    <FormSelectInput
                      label={t("pricingForm.alternativeTaxCalculationType")}
                      name="alternativeTaxCalculationType"
                      selectArray={calcModeOptions}
                      defaultValue={TaxCalculationTypeEnum.AMOUNT}
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <br></br>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <CurrencyInput
                label={t("pricingForm.priceWithoutTaxes")}
                name="price"
                value={values.price || 0}
                error={!!(errors.price && touched.price)}
                helperText={errors.price && touched.price ? errors.price : ""}
                onChange={(value) => setFieldValue("price", value)}
                fullWidth
                disabled={true}
                className={darkMode ? "currency-input--dark" : "currency-input"}
                inputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  style: darkMode ? {
                    color: '#fff',
                  } : undefined,
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }} className="form-container-action-buttons">
            {!isView && (
              <Grid item xs={12} sm="auto" md="auto">
                {isNew ? (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.PRICING].CREATE]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.submit")}
                    </Button>
                  </PermissionWrapper>
                ) : (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.PRICING].EDIT]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.update")}
                    </Button>
                  </PermissionWrapper>
                )}
              </Grid>
            )}
            <Grid item xs={12} sm="auto" md="auto">
              <Button
                variant="contained"
                color="secondary"
                onClick={onCloseAction}
              >
                {t("common.close")}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </FormikForm>
  );
};

export default PricingForm;